import React, { type ReactNode } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import InfoIcon from '@atlaskit/icon/core/migration/information--info';
import PremiumIcon from '@atlaskit/icon/core/migration/premium';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { MaybeChoreographedComponentProps } from '@atlassian/jira-choreographer-services/src/types.tsx';
import Flag from '@atlassian/jira-flags/src/common/ui/components/ak-flag';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { CustomDomainStatus, HelpCenterUrls } from '../../services/use-properties/types.tsx';
import messages from './messages.tsx';

type Props = {
	onDismissed: (id: string | number, analyticsEvent: UIAnalyticsEvent) => void;
	onClick: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	status: CustomDomainStatus;
	helpCenterUrls: HelpCenterUrls;
} & Partial<MaybeChoreographedComponentProps>;
const Bold = (props: { children: ReactNode }) => <Text as="strong">{props.children}</Text>;

export const FlagInner = (props: Props) => {
	const {
		onDismissed,
		onClick,
		status,
		helpCenterUrls,
		messageId = 'help-center-custom-domain-change-boarding.ui.flag-inner.flag',
		messageType = 'transactional',
	} = props;
	const { formatMessage } = useIntl();

	return (
		<Flag
			id="jsm-custom-domain-change-boarding-flag"
			icon={
				status === 'ACTIVE' ? (
					<PremiumIcon spacing="spacious" label="" color={token('color.icon.accent.purple')} />
				) : (
					<InfoIcon spacing="spacious" label="" color={token('color.icon.brand')} />
				)
			}
			title={
				status === 'ACTIVE'
					? formatMessage(messages.flagTitle)
					: formatMessage(messages.flagTitleInactive)
			}
			description={
				status === 'ACTIVE' ? (
					<FormattedI18nMessage
						message={formatMessage(messages.flagDescription, {
							boldStart: '{boldStart}',
							boldEnd: '{boldEnd}',
						})}
						componentsMapping={{ bold: Bold }}
					/>
				) : (
					<FormattedI18nMessage
						message={formatMessage(messages.flagDescriptionInactive, {
							boldStart: '{boldStart}',
							boldEnd: '{boldEnd}',
							oldUrl: helpCenterUrls.oldUrl,
							newUrl: helpCenterUrls.newUrl,
						})}
						componentsMapping={{ bold: Bold }}
					/>
				)
			}
			messageId={messageId}
			messageType={messageType}
			actions={[
				{
					...(status === 'ACTIVE'
						? {
								// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
								content: <p>{formatMessage(messages.flagActionContent)}</p>,
								onClick,
							}
						: {
								// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
								content: <p>{formatMessage(messages.flagActionContentInactivateOrRemove)} </p>,
								onClick,
							}),
				},
			]}
			onDismissed={onDismissed}
		/>
	);
};
